import { Layers, GitPullRequest, Search } from "lucide-react";

const pillars = [
  {
    icon: Layers,
    title: "Evidence-backed Feature Models",
    subtitle: "See how a feature actually works across code, state, contracts, dependencies, and tests, not just where its files live.",
    bullets: [
      "Feature Flows capture execution paths, branches, side effects, and system boundaries.",
      "Every meaningful fact stays anchored to a source file, symbol, and commit.",
      "Explicit constraints and confidence make the model inspectable instead of magical.",
    ],
    mockLabel: "Scheduled Tasks · 8 execution flows · 130 source-backed connections",
    accent: true,
  },
  {
    icon: GitPullRequest,
    title: "Cross-repository Feature Trails",
    subtitle: "Follow behavior across services and repositories, even where no call graph can connect the pieces for you.",
    bullets: [
      "Feature Trails connect independently deployed services into one understandable path.",
      "Cross-repository links are verified on both sides of every boundary.",
      "Unknown and private parts of a flow stay visible instead of being guessed away.",
    ],
    mockLabel: "Follow a transaction from ingestion to storage across all your repositories, with every service handoff linked to source.",
    accent: false,
  },
  {
    icon: Search,
    title: "Task-specific AI Context",
    subtitle: "Turn each engineering task into the smallest relevant slice of the system, backed by the source it came from.",
    bullets: [
      "Resolve the relevant feature, flow, conditions, dependencies, and tests.",
      "Reduce repeated repository exploration without hiding critical context.",
      "Keep existing coding agents in the loop. AntAtlas provides understanding, not another agent.",
    ],
    mockLabel: "Task → relevant subgraph → evidence-backed AI context",
    accent: false,
  },
];

const ProductPillars = () => (
  <section className="section-padding" id="product">
    <div className="container-wide">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <p className="text-xs font-medium uppercase tracking-widest text-accent mb-3">
          Product
        </p>
        <h2 className="heading-lg mb-4">One live model. The right context for every task.</h2>
        <p className="body-md text-muted-foreground">
          AntAtlas maps how your system works, keeps every connection tied to source, and gives coding agents only what matters now.
        </p>
      </div>

      <div className="space-y-20">
        {pillars.map((p, i) => (
          <div
            key={p.title}
            className={`flex flex-col lg:flex-row items-center gap-10 lg:gap-16 ${
              i % 2 === 1 ? "lg:flex-row-reverse" : ""
            }`}
          >
            {/* Text */}
            <div className="flex-1 max-w-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2.5 rounded-lg ${p.accent ? "bg-accent/10 text-accent neon-glow-accent" : "bg-secondary text-foreground neon-glow-blue"}`}>
                  <p.icon className={`h-5 w-5 ${p.accent ? "drop-shadow-[0_0_6px_hsl(16_85%_56%/0.5)]" : "drop-shadow-[0_0_6px_hsl(220_80%_60%/0.4)]"}`} />
                </div>
                <h3 className="heading-md">{p.title}</h3>
              </div>
              <p className="body-md text-muted-foreground mb-5">{p.subtitle}</p>
              <ul className="space-y-3">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm leading-relaxed">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent shrink-0 neon-dot" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            {/* Mock */}
            <div className="flex-1 w-full">
              <div className="surface-card p-8 md:p-10 min-h-[240px] flex flex-col items-center justify-center text-center neon-border-accent hover:neon-glow-accent transition-shadow duration-500">
                <div className="flex gap-2 mb-4">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  <GitPullRequest className="h-4 w-4 text-muted-foreground" />
                  <Search className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground font-mono">{p.mockLabel}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Integrations note */}
      <div className="mt-20 text-center surface-card p-8 max-w-2xl mx-auto">
        <p className="text-sm font-semibold mb-2">Built for the agents you already use</p>
        <p className="text-sm text-muted-foreground">
          AntAtlas is the live system context layer. Your coding agent still does the work, whether that is{" "}
          <strong className="text-foreground">Claude Code</strong>, <strong className="text-foreground">Cursor</strong>,{" "}
          <strong className="text-foreground">Codex</strong>, or another agent in your workflow.
        </p>
      </div>
    </div>
  </section>
);

export default ProductPillars;
